import { useNavigate } from 'react-router-dom';
import { Flame, Bookmark, AlertTriangle } from 'lucide-react';
import Chip, { getBudgetStatusVariant } from '../Chip';
import { BudgetItem, BUDGET_POSITIONS } from '../../types/budget';
import { getPositionColorClasses } from '../../utils/positionColors';
import EmptyState from '../EmptyState';

interface BudgetCardsProps {
  items: BudgetItem[];
  context: 'budget' | 'workspace';
  workspaceId?: string;
  onBookmark?: (item: BudgetItem) => void;
  onToggleHot?: (itemId: string, isHot: boolean) => void;
  onUpdatePosition?: (itemId: string, position: string) => void;
  onToggleReview?: (itemId: string, needsReview: boolean) => void;
  showCheckboxes?: boolean;
  selectedItems?: Set<string>;
  onItemSelection?: (itemId: string) => void;
  canEdit?: boolean;
}

const BudgetCards: React.FC<BudgetCardsProps> = ({
  items,
  context,
  workspaceId,
  onBookmark,
  onToggleHot,
  onUpdatePosition,
  onToggleReview,
  showCheckboxes = false,
  selectedItems,
  onItemSelection,
  canEdit = false,
}) => {
  const navigate = useNavigate();

  const handleCardClick = (item: BudgetItem) => {
    const params = workspaceId ? `?workspace=${workspaceId}` : '';
    navigate(`/budget/${item.id}${params}`);
  };

  const getItemNumber = (item: BudgetItem) => item.itemNumber || item.item_number;
  const getItemTitle = (item: BudgetItem) => item.itemTitle || item.item_title;
  const getFiscalYear = (item: BudgetItem) => item.fiscalYear || item.fiscal_year;
  const getDeptName = (item: BudgetItem) => item.deptName || item.dept_name;
  const getIsHot = (item: BudgetItem) => item.isHot ?? item.tracked_is_hot ?? false;
  const getPosition = (item: BudgetItem) => item.position || item.tracked_position;
  const getReview = (item: BudgetItem) => item.needsClientReview ?? item.tracked_review ?? false;

  if (items.length === 0) {
    return (
      <EmptyState hint="No budget items found." />
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {items.map((item) => {
        const isSelected = selectedItems?.has(String(item.id));
        const hot = getIsHot(item);
        const position = getPosition(item);
        const review = getReview(item);

        return (
          <div
            key={item.id}
            className={`card p-4 cursor-pointer hover:shadow-md transition-shadow ${
              isSelected ? 'ring-2 ring-primary-500' : ''
            } ${hot ? 'border-l-4 border-l-orange-500' : ''}`}
            onClick={() => {
              if (showCheckboxes && onItemSelection) {
                onItemSelection(String(item.id));
              } else {
                handleCardClick(item);
              }
            }}
          >
            {/* Header: Item Number + Actions */}
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                {showCheckboxes && (
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onItemSelection?.(String(item.id))}
                    onClick={(e) => e.stopPropagation()}
                    className="w-4 h-4 text-primary-600 rounded border-border-light dark:border-border-dark focus:ring-primary-500"
                  />
                )}
                <span className="text-sm font-semibold text-primary-700 dark:text-primary-400">
                  {getItemNumber(item)}
                </span>
                {hot && <Flame className="w-4 h-4 text-orange-500" />}
                {review && <AlertTriangle className="w-4 h-4 text-warning-500" />}
              </div>

              <div className="flex items-center gap-1">
                {position && (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getPositionColorClasses(position)}`}>
                    {BUDGET_POSITIONS.find((p) => p.value === position)?.label || position}
                  </span>
                )}
                {context === 'budget' && onBookmark && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onBookmark(item);
                    }}
                    className="p-1.5 rounded-lg hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary text-text-tertiary dark:text-text-dark-tertiary"
                  >
                    <Bookmark className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Title */}
            <h3 className="text-sm font-medium text-text-primary dark:text-text-dark-primary mb-2 line-clamp-2">
              {getItemTitle(item)}
            </h3>

            {/* Metadata Row */}
            <div className="flex flex-wrap items-center gap-2 text-xs text-text-secondary dark:text-text-dark-secondary">
              <span>{getDeptName(item)}</span>
              <span className="text-text-tertiary dark:text-text-dark-tertiary">•</span>
              <span>FY {getFiscalYear(item)}</span>
              <Chip
                label={item.status}
                variant={getBudgetStatusVariant(item.status)}
                size="sm"
              />
            </div>

            {/* Workspace context: Position select + actions */}
            {context === 'workspace' && canEdit && (
              <div className="mt-3 pt-3 border-t border-border-light dark:border-border-dark flex items-center gap-2">
                <select
                  value={position || ''}
                  onChange={(e) => {
                    e.stopPropagation();
                    onUpdatePosition?.(String(item.id), e.target.value);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="flex-1 text-xs px-2 py-1 border border-border-light dark:border-border-dark rounded bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary"
                >
                  <option value="">Set Position</option>
                  {BUDGET_POSITIONS.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleHot?.(String(item.id), !hot);
                  }}
                  className={`p-1 rounded ${hot ? 'text-orange-500' : 'text-text-tertiary dark:text-text-dark-tertiary'}`}
                  title={hot ? 'Remove hot' : 'Mark as hot'}
                >
                  <Flame className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleReview?.(String(item.id), !review);
                  }}
                  className={`p-1 rounded ${review ? 'text-warning-500' : 'text-text-tertiary dark:text-text-dark-tertiary'}`}
                  title={review ? 'Clear review flag' : 'Flag for review'}
                >
                  <AlertTriangle className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default BudgetCards;
